/**
 * Formatadores de valores numéricos para pt-BR.
 *
 * Todos os helpers são deterministas (mesma entrada → mesma saída) e seguem
 * convenções brasileiras: separador de milhar com ponto, decimal com vírgula.
 *
 * Performance: usa Intl.NumberFormat instanciado lazy + cache estático.
 */

// Caches de Intl.NumberFormat — instanciar é caro, reusar é grátis
const _currencyFormatters = new Map<string, Intl.NumberFormat>();
const _numberFormatters = new Map<string, Intl.NumberFormat>();

// Node 20+/ICU recente devolve NBSP (U+00A0) e narrow NBSP (U+202F) como separadores
// (entre símbolo de moeda e número, em milhares "de", etc). Normalizamos pra ASCII space
// pra (a) bater com testes determinísticos, (b) não quebrar copy/paste pra Excel, (c) permitir
// busca textual por "R$ 24.800". Quem precisar de NBSP renderiza com CSS `white-space: nowrap`.
function normalizeSpaces(s: string): string {
  return s.replace(/[\u00A0\u202F]/g, ' ');
}

function getCurrencyFormatter(currency: string, minFractionDigits: number, maxFractionDigits: number): Intl.NumberFormat {
  const key = `${currency}|${minFractionDigits}|${maxFractionDigits}`;
  let f = _currencyFormatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
    });
    _currencyFormatters.set(key, f);
  }
  return f;
}

function getNumberFormatter(minFractionDigits: number, maxFractionDigits: number): Intl.NumberFormat {
  const key = `${minFractionDigits}|${maxFractionDigits}`;
  let f = _numberFormatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
    });
    _numberFormatters.set(key, f);
  }
  return f;
}

// ============================================================
// MOEDA
// ============================================================

export interface FormatCurrencyOptions {
  /** Código ISO 4217 — default: 'BRL' */
  currency?: string;
  /** Mínimo de casas decimais — default: 2 */
  minFractionDigits?: number;
  /** Máximo de casas decimais — default: 2 */
  maxFractionDigits?: number;
}

/**
 * Formata número em moeda BR.
 *
 * formatCurrency(24800)            → "R$ 24.800,00"
 * formatCurrency(24800.5)          → "R$ 24.800,50"
 * formatCurrency(0)                → "R$ 0,00"
 * formatCurrency(null)             → "—"
 * formatCurrency(1500, { currency: 'USD' }) → "US$ 1.500,00"
 */
export function formatCurrency(
  value: number | null | undefined,
  options: FormatCurrencyOptions = {}
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const { currency = 'BRL', minFractionDigits = 2, maxFractionDigits = 2 } = options;
  return normalizeSpaces(
    getCurrencyFormatter(currency, minFractionDigits, maxFractionDigits).format(value)
  );
}

/**
 * Formata moeda em formato abreviado pra dashboards (R$ 24,8 mil / R$ 1,2 mi).
 *
 * formatCurrencyCompact(24800)       → "R$ 24,8 mil"
 * formatCurrencyCompact(1_234_567)   → "R$ 1,23 mi"
 * formatCurrencyCompact(2_500_000_000) → "R$ 2,5 bi"
 * formatCurrencyCompact(450)         → "R$ 450,00"
 */
export function formatCurrencyCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs < 1000) {
    return formatCurrency(value);
  }
  if (abs < 1_000_000) {
    return normalizeSpaces(`${sign}R$ ${getNumberFormatter(1, 1).format(abs / 1000)} mil`);
  }
  if (abs < 1_000_000_000) {
    return normalizeSpaces(`${sign}R$ ${getNumberFormatter(1, 2).format(abs / 1_000_000)} mi`);
  }
  return normalizeSpaces(`${sign}R$ ${getNumberFormatter(1, 2).format(abs / 1_000_000_000)} bi`);
}

// ============================================================
// NÚMEROS
// ============================================================

/**
 * Formata número com separadores BR.
 *
 * formatNumber(1234567.89)              → "1.234.567,89"
 * formatNumber(1234, { decimals: 0 })   → "1.234"
 * formatNumber(null)                    → "—"
 */
export function formatNumber(
  value: number | null | undefined,
  options: { decimals?: number; minDecimals?: number } = {}
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const { decimals = 2, minDecimals = decimals } = options;
  return normalizeSpaces(getNumberFormatter(minDecimals, decimals).format(value));
}

/**
 * Formata porcentagem.
 *
 * formatPercent(0.245)             → "24,5%"
 * formatPercent(0.245, { fromUnit: true })  → "24,5%"
 * formatPercent(24.5, { fromUnit: false })  → "24,5%"
 *
 * @param fromUnit Se true (default), entrada é fração (0.245 = 24,5%).
 *                 Se false, entrada já está em pontos percentuais (24.5 = 24,5%).
 */
export function formatPercent(
  value: number | null | undefined,
  options: { decimals?: number; fromUnit?: boolean } = {}
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const { decimals = 1, fromUnit = true } = options;
  const v = fromUnit ? value * 100 : value;
  return normalizeSpaces(`${getNumberFormatter(decimals, decimals).format(v)}%`);
}

// ============================================================
// PARSING (string → number) — útil em forms
// ============================================================

/**
 * Converte string em formato BR para número.
 *
 * parseCurrency("R$ 24.800,50")  → 24800.5
 * parseCurrency("1.234,56")      → 1234.56
 * parseCurrency("100")           → 100
 * parseCurrency("")              → null
 * parseCurrency("inválido")      → null
 */
export function parseCurrency(input: string | null | undefined): number | null {
  if (!input) return null;
  // Remove tudo que não for dígito, vírgula, ponto, sinal
  const cleaned = input.replace(/[^\d,.-]/g, '');
  if (!cleaned) return null;
  // BR usa ponto como separador de milhar e vírgula como decimal
  const normalized = cleaned.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Converte um input numérico simples (um número só) em number, aceitando
 * vírgula OU ponto como separador decimal. Pra campos como diária, quantidade e
 * custo — onde o usuário digita "1,5" ou "1.5".
 *
 * 🔴 LOCACAO-NAO-SALVA (31/08/2026): quando há VÍRGULA, o ponto é separador de
 * milhar — "1.500,50" só pode ser mil e quinhentos reais e cinquenta centavos.
 * Antes essa forma virava `NaN` e o campo era descartado em silêncio. Sem
 * vírgula o ponto continua sendo decimal ("1.5" → 1.5), como sempre foi — a
 * mudança só resgata o que antes não tinha leitura nenhuma.
 *
 * parseNumeroBr("1,5")        → 1.5
 * parseNumeroBr("1.5")        → 1.5
 * parseNumeroBr("1.500,50")   → 1500.5
 * parseNumeroBr("10")         → 10
 * parseNumeroBr("")           → null
 * parseNumeroBr("abc")        → null
 */
export function parseNumeroBr(input: string | null | undefined): number | null {
  if (input == null) return null;
  const s = String(input).trim();
  if (!s) return null;
  const semMilhar = s.includes(',') ? s.replace(/\./g, '') : s;
  const n = Number(semMilhar.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/**
 * Converte uma string em reais (aceita vírgula BR) para CENTAVOS inteiros.
 * Vazio/negativo/inválido → 0. Extraído do módulo de Compras (CO-4, 2026-06-23)
 * pra virar fonte única (era duplicado por tela).
 */
export function reaisStringToCentavos(input: string | null | undefined): number {
  const n = parseNumeroBr(input);
  return n !== null && n >= 0 ? Math.round(n * 100) : 0;
}
